// "Sync my email history" — the delegated email→history flow (design
// approved 2026-07-15). Scans the SIGNED-IN USER'S OWN mailbox for threads
// with this account's contacts (auth/emailSync.ts), lets them exclude
// threads before anything leaves the browser, then sends the selection to
// the API which stores an LLM summary per thread (never the bodies).
import { useEffect, useMemo, useState } from 'react'
import { Mail } from 'lucide-react'

import { Modal, Button } from '@/components/ui'
import { useStore } from '@/store/useStore'
import { useApiToken } from '@/auth/useApiToken'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { api, ApiError } from '@/api/client'
import { fetchAccountThreads, type EmailThread } from '@/auth/emailSync'
import { date, todayIso } from '@/lib/format'
import type { Account, ActionItem, Activity, Contact } from '@/types/entities'

const BATCH_SIZE = 10 // API caps at 15/request

type Phase = 'scanning' | 'review' | 'submitting' | 'actions' | 'done' | 'error'

/** A summarizer-suggested task awaiting the user's yes/no. */
interface SuggestedTask {
  key: string
  title: string
  dueDate?: string
  owner?: string
  threadTitle: string
}

const newTaskId = () =>
  `ai-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export function SyncEmailModal({
  open, onClose, account, contacts,
}: { open: boolean; onClose: () => void; account: Account; contacts: Contact[] }) {
  const getToken = useApiToken()
  const user = useCurrentUser()
  const upsert = useStore((s) => s.upsert)
  const [phase, setPhase] = useState<Phase>('scanning')
  const [threads, setThreads] = useState<EmailThread[]>([])
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [counts, setCounts] = useState({ created: 0, updated: 0, unchanged: 0, errors: 0 })
  const [suggestions, setSuggestions] = useState<SuggestedTask[]>([])
  const [excludedTasks, setExcludedTasks] = useState<Set<string>>(new Set())
  const [tasksCreated, setTasksCreated] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')

  const contactName = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of contacts) m.set(c.id, `${c.firstName} ${c.lastName}`.trim())
    return m
  }, [contacts])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPhase('scanning')
    setExcluded(new Set())
    setSuggestions([])
    setExcludedTasks(new Set())
    setTasksCreated(0)
    fetchAccountThreads(contacts)
      .then((t) => { if (!cancelled) { setThreads(t); setPhase('review') } })
      .catch((err) => {
        if (cancelled) return
        setErrorMsg(err instanceof Error ? err.message : String(err))
        setPhase('error')
      })
    return () => { cancelled = true }
    // Contacts are stable for an open modal; re-scan only on reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selected = threads.filter((t) => !excluded.has(t.conversationId))

  const toggle = (id: string) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const submit = async () => {
    setPhase('submitting')
    setProgress({ done: 0, total: selected.length })
    const tally = { created: 0, updated: 0, unchanged: 0, errors: 0 }
    const newActivities: Activity[] = []
    const suggested: SuggestedTask[] = []
    try {
      const token = await getToken()
      for (let i = 0; i < selected.length; i += BATCH_SIZE) {
        const batch = selected.slice(i, i + BATCH_SIZE)
        const { results } = await api.emailIngest(token, {
          accountId: account.id,
          accountName: account.name,
          threads: batch,
        })
        for (const r of results) {
          if (r.status === 'error') tally.errors += 1
          else tally[r.status] += 1
          if (r.activity && r.status !== 'unchanged') newActivities.push(r.activity as Activity)
          for (const [j, item] of (r.actionItems ?? []).entries()) {
            suggested.push({ key: `${r.id}:${j}`, threadTitle: r.title, ...item })
          }
        }
        setProgress({ done: Math.min(i + batch.length, selected.length), total: selected.length })
      }
      // Local-only merge — the server already persisted these; writing through
      // the store's upsert would double-write.
      if (newActivities.length) {
        useStore.setState((s) => {
          const byId = new Map(s.data.activities.map((a) => [a.id, a]))
          for (const a of newActivities) byId.set(a.id, a)
          return { data: { ...s.data, activities: [...byId.values()] } }
        })
      }
      setCounts(tally)
      setSuggestions(suggested)
      setPhase(suggested.length > 0 ? 'actions' : 'done')
    } catch (err) {
      setErrorMsg(err instanceof ApiError && err.status === 503
        ? 'Summarization is not configured for this environment yet.'
        : err instanceof Error ? err.message : String(err))
      setPhase('error')
    }
  }

  const toggleTask = (key: string) => {
    setExcludedTasks((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedTasks = suggestions.filter((s) => !excludedTasks.has(s.key))

  /** The assignee for a suggested task: the WPI person the summarizer named,
   *  else the signed-in user (they synced it; it's their follow-up). */
  const assigneeFor = (s: SuggestedTask): { owner: string; ownerEmail?: string } => {
    if (s.owner && s.owner.endsWith('@westportproperties.net')) {
      const name = s.owner.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      return { owner: name, ownerEmail: s.owner }
    }
    return { owner: user.displayName, ownerEmail: user.email || undefined }
  }

  const createTasks = () => {
    for (const s of selectedTasks) {
      const item: ActionItem = {
        id: newTaskId(),
        title: s.title,
        priority: 'Medium',
        status: 'Open',
        ...assigneeFor(s),
        dueDate: s.dueDate,
        source: `Email sync · ${s.threadTitle}`,
        createdAt: todayIso(),
        attachedTo: { kind: 'account', id: account.id, label: account.name },
      }
      upsert('actionItems', item)
    }
    setTasksCreated(selectedTasks.length)
    setPhase('done')
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<><Mail size={16} style={{ marginRight: 8, verticalAlign: -2 }} />Sync my email history — {account.name}</>}
      width={640}
      footer={
        phase === 'review' ? (
          <>
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={selected.length === 0} onClick={() => void submit()}>
              Summarize {selected.length} thread{selected.length === 1 ? '' : 's'}
            </Button>
          </>
        ) : phase === 'actions' ? (
          <>
            <Button variant="default" onClick={() => setPhase('done')}>Skip</Button>
            <Button variant="primary" disabled={selectedTasks.length === 0} onClick={createTasks}>
              Create {selectedTasks.length} task{selectedTasks.length === 1 ? '' : 's'}
            </Button>
          </>
        ) : phase === 'done' || phase === 'error' ? (
          <Button variant="primary" onClick={onClose}>Close</Button>
        ) : undefined
      }
    >
      {phase === 'scanning' && (
        <p className="muted">Searching your mailbox for threads with {account.name}&rsquo;s contacts (last 18 months)&hellip;</p>
      )}

      {phase === 'review' && threads.length === 0 && (
        <p className="muted">No email threads with this account&rsquo;s contacts were found in your mailbox.</p>
      )}

      {phase === 'review' && threads.length > 0 && (
        <>
          <p className="muted" style={{ marginBottom: 12 }}>
            {threads.length} thread{threads.length === 1 ? '' : 's'} found. Untick anything that
            shouldn&rsquo;t be on the account history. Only an AI-written <b>summary</b> of each
            thread is stored — the emails themselves never leave your mailbox.
          </p>
          <div style={{ maxHeight: 340, overflowY: 'auto', display: 'grid', gap: 6 }}>
            {threads.map((t) => (
              <label
                key={t.conversationId}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', border: '1px solid var(--border, #e2e4e9)', borderRadius: 8, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={!excluded.has(t.conversationId)}
                  onChange={() => toggle(t.conversationId)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject || '(no subject)'}
                  </span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {t.contactIds.map((id) => contactName.get(id) ?? id).join(', ')}
                    {' · '}{t.messageCount} message{t.messageCount === 1 ? '' : 's'}
                    {' · '}{date(t.firstMessageAt, 'short')} – {date(t.lastMessageAt, 'short')}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      {phase === 'submitting' && (
        <p className="muted">Summarizing thread {Math.min(progress.done + 1, progress.total)} of {progress.total}&hellip; this can take a moment.</p>
      )}

      {phase === 'actions' && (
        <>
          <p className="muted" style={{ marginBottom: 12 }}>
            The summaries mention things that look like open follow-ups. Untick anything that
            isn&rsquo;t (or is already handled) — the rest become <b>tasks</b> on this account.
          </p>
          <div style={{ maxHeight: 340, overflowY: 'auto', display: 'grid', gap: 6 }}>
            {suggestions.map((s) => {
              const assignee = assigneeFor(s)
              return (
                <label
                  key={s.key}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', border: '1px solid var(--border, #e2e4e9)', borderRadius: 8, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={!excludedTasks.has(s.key)}
                    onChange={() => toggleTask(s.key)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600 }}>{s.title}</span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {assignee.owner}
                      {s.dueDate ? <>{' · due '}{date(s.dueDate, 'short')}</> : null}
                      {' · from '}{s.threadTitle || '(no subject)'}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </>
      )}

      {phase === 'done' && (
        <p>
          Done — {counts.created} added, {counts.updated} updated,
          {' '}{counts.unchanged} already current{counts.errors ? <>, <b>{counts.errors} failed</b> (retry later)</> : ''}.
          Summaries appear on the account&rsquo;s <b>History</b> tab.
          {tasksCreated > 0 && <>{' '}{tasksCreated} task{tasksCreated === 1 ? '' : 's'} created — see the <b>Action Items</b> tab.</>}
        </p>
      )}

      {phase === 'error' && <p style={{ color: 'var(--danger, #b3261e)' }}>{errorMsg}</p>}
    </Modal>
  )
}
